import { useRouter } from 'next/router';
import * as contentful from 'contentful';
import { useMemo } from 'react';
import { ContentfulLivePreview } from '@contentful/live-preview';

declare global {
  interface Window {
    Cypress?: any;
  }
}

const shouldInitializeLivePreview = () => {  
  // Only run in browser environment
  if (typeof window === 'undefined') {
    return false;
  }
  
  // Don't initialize if we're in Cypress
  if (window.Cypress || window.navigator?.userAgent?.includes('Cypress')) {
    return false;
  }
  
  // Don't initialize if we don't have a proper origin
  if (!window.location?.origin || window.location.origin === 'null') {
    return false;
  }
  
  return true;
};

if (shouldInitializeLivePreview()) {
  ContentfulLivePreview.init({
    locale: 'en-US',
    enableLiveUpdates: true,
    enableInspectorMode: true,
    debugMode: true,
  });
}

export const useContentfulClient =
  (): contentful.ContentfulClientApi<undefined> => {
    const router = useRouter();
    const isPreview = router?.query?.preview === 'true';

    const client = useMemo(
      () =>
        contentful.createClient({
          space: process.env.NEXT_PUBLIC_CONTENTFUL_SPACE_ID,
          accessToken: isPreview
            ? process.env.NEXT_PUBLIC_CONTENTFUL_PREVIEW_ACCESS_TOKEN
            : process.env.NEXT_PUBLIC_CONTENTFUL_ACCESS_TOKEN,
          host: isPreview ? 'preview.contentful.com' : 'cdn.contentful.com',
          environment:
            process.env.NEXT_PUBLIC_CONTENTFUL_ENVIRONMENT || 'master',
        }),
      [isPreview]
    );

    return client;
  };
